import type { Article, FinalResults, Gazette, Locale, PublicPlayer, Verdict } from '@gazette/shared';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { headlineText } from './cards.ts';
import { INK, INK_SOFT, NEWSPRINT, THREAD } from './palette.ts';
import { portraitCanvas } from './portrait.ts';
import { fonts, inkWear, paperGrain, wrapAround, wrapText } from './text.ts';

// The whole edition as one broadsheet page: the texture of the spinning newspaper at
// publication, and the image players download at the end.

export interface GazetteInput {
  gazette: Gazette;
  players: PublicPlayer[];
  locale: Locale;
  /** Verdicts to print as stamps and bylines; empty while the edition is still a mystery. */
  verdicts: Verdict[];
  results: FinalResults | null;
  footer?: string;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface GazetteRender {
  canvas: HTMLCanvasElement;
  /** Article boxes in UV space (0-1, origin top-left), for cutting clippings out in 3D. */
  rects: Map<string, Rect>;
  aspect: number;
}

const W = 2048;
const M = 96;
const G = 46;
const MAX_TEXTURE = 4096;

type Draw = (ctx: CanvasRenderingContext2D) => void;

interface Block {
  height: number;
  draw: (ctx: CanvasRenderingContext2D, x: number, y: number) => void;
}

function measureCtx(): CanvasRenderingContext2D {
  return document.createElement('canvas').getContext('2d')!;
}

function stampBox(ctx: CanvasRenderingContext2D, label: string, cx: number, cy: number, size: number, angle: number, seed: number) {
  const art = document.createElement('canvas');
  art.width = Math.round(size * 3.2);
  art.height = Math.round(size * 1.35);
  const a = art.getContext('2d')!;
  a.strokeStyle = THREAD;
  a.fillStyle = THREAD;
  a.lineWidth = size * 0.07;
  a.strokeRect(a.lineWidth, a.lineWidth, art.width - a.lineWidth * 2, art.height - a.lineWidth * 2);
  a.font = fonts.display(size);
  a.textAlign = 'center';
  a.textBaseline = 'middle';
  a.fillText(label, art.width / 2, art.height / 2 + size * 0.05);
  inkWear(a, art.width, art.height, seed, 0.8);
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(angle);
  ctx.globalAlpha = 0.86;
  ctx.globalCompositeOperation = 'multiply';
  ctx.drawImage(art, -art.width / 2, -art.height / 2);
  ctx.restore();
}

function articleBlock(
  article: Article,
  input: GazetteInput,
  width: number,
  lead: boolean,
): Block {
  const ctx = measureCtx();
  const victim = input.players.find((player) => player.id === article.targetId);
  const verdict = input.verdicts.find((candidate) => candidate.articleId === article.id);
  const author = verdict ? input.players.find((player) => player.id === verdict.authorId) : undefined;
  const photoW = lead ? 340 : 118;
  const photoH = Math.round(photoW * 1.25);
  const headSize = lead ? 94 : 50;
  const headLead = Math.round(headSize * 1.1);
  const subSize = lead ? 44 : 29;
  const subLead = Math.round(subSize * 1.3);

  ctx.font = fonts.news(headSize, lead ? 900 : 800);
  const headline = headlineText(article.kicker, article.headline, input.locale);
  const narrow = width - photoW - 28;
  const head = lead
    ? wrapText(ctx, headline, narrow)
    : wrapAround(ctx, headline, width, narrow, Math.ceil((photoH + 10) / headLead));
  ctx.font = fonts.news(subSize, 400, true);
  const sub = article.subtitle ? wrapText(ctx, article.subtitle, lead ? narrow : width) : [];

  const top = lead ? 0 : 44;
  // Mirrors the cursor arithmetic in draw(): first baseline, lines, standfirst, then the
  // "concerns" line, pushed below the portrait on small articles, and the byline.
  const textBottom = top + headSize * 0.86 + head.length * headLead + (sub.length ? 10 + sub.length * subLead : 0) + 18;
  const concernsAt = lead ? textBottom : Math.max(textBottom, top + photoH + 36);
  const height = Math.max(concernsAt + (author ? 40 : 0) + 30, top + photoH + 40) + (lead ? 24 : 20);

  return {
    height,
    draw(c, x, y) {
      c.fillStyle = INK;
      if (!lead) {
        c.font = fonts.news(24, 700);
        c.fillText(t('gz.edition', { n: article.number }, input.locale), x, y + 22);
        c.fillRect(x, y + 32, 60, 2);
      }
      if (victim) {
        c.drawImage(portraitCanvas(victim.portrait, photoW, photoH), x + width - photoW, y + top);
      }
      let cursor = y + top + headSize * 0.86;
      c.font = fonts.news(headSize, lead ? 900 : 800);
      for (const line of head) {
        c.fillText(line, x, cursor);
        cursor += headLead;
      }
      cursor += sub.length ? 10 : 0;
      c.fillStyle = INK_SOFT;
      c.font = fonts.news(subSize, 400, true);
      for (const line of sub) {
        c.fillText(line, x, cursor);
        cursor += subLead;
      }
      c.fillStyle = INK;
      c.font = fonts.news(lead ? 28 : 22, 700);
      const concerns = t('gz.concerns', { name: victim?.name ?? '?' }, input.locale).toUpperCase();
      cursor = Math.max(cursor + 18, lead ? 0 : y + top + photoH + 36);
      c.fillText(concerns, x, cursor);
      if (author) {
        c.font = fonts.news(lead ? 34 : 26, 400, true);
        c.fillText(t('gz.signed', { name: author.name }, input.locale), x, cursor + 40);
      }
      if (verdict) {
        const label = t(verdict.isTrue ? 'rev.true' : 'rev.false', undefined, input.locale).toUpperCase();
        const size = lead ? 120 : 76;
        stampBox(c, label, x + width - photoW * 0.5 - (lead ? 20 : 30), y + top + photoH * 0.72, size, verdict.isTrue ? -0.14 : 0.12, article.number * 31);
      }
      c.fillStyle = INK;
      c.fillRect(x, y + height - 14, width, lead ? 3 : 1.5);
    },
  };
}

function adBlock(key: MessageKey, locale: Locale, width: number): Block {
  const ctx = measureCtx();
  ctx.font = fonts.news(28, 400);
  const lines = wrapText(ctx, t(key, undefined, locale), width - 48);
  const height = 48 + lines.length * 36 + 40;
  return {
    height,
    draw(c, x, y) {
      c.strokeStyle = INK;
      c.lineWidth = 3;
      c.strokeRect(x + 2, y + 2, width - 4, height - 30);
      c.lineWidth = 1;
      c.strokeRect(x + 10, y + 10, width - 20, height - 46);
      c.fillStyle = INK;
      c.font = fonts.news(28, 400);
      let cursor = y + 56;
      for (const line of lines) {
        c.fillText(line, x + 24, cursor);
        cursor += 36;
      }
    },
  };
}

function palmaresBlock(input: GazetteInput, width: number): Block | null {
  const results = input.results;
  if (!results) return null;
  const name = (id: string) => input.players.find((player) => player.id === id)?.name ?? '?';
  const podium = results.ranking.slice(0, 5).map((entry) => `${entry.rank}. ${name(entry.playerId)}, ${t('final.points', { n: entry.score }, input.locale)}`);
  const colon = input.locale === 'fr' ? '\u00A0: ' : ': ';
  const titles = results.titles.map((title) => `${t(`title.${title.id}`, undefined, input.locale)}${colon}${title.playerIds.map(name).join(', ')}`);
  const lines = [...podium, ...titles];
  const height = 110 + Math.ceil(lines.length / 2) * 50 + 30;
  return {
    height,
    draw(c, x, y) {
      c.fillStyle = INK;
      c.fillRect(x, y, width, 6);
      c.font = fonts.display(64);
      c.fillText(t('gz.palmares', undefined, input.locale).toUpperCase(), x, y + 76);
      c.font = fonts.news(32, 400);
      lines.forEach((line, index) => {
        const column = index % 2;
        const row = Math.floor(index / 2);
        c.fillText(line, x + column * (width / 2), y + 132 + row * 50);
      });
    },
  };
}

export function renderGazette(input: GazetteInput): GazetteRender {
  const { gazette, locale } = input;
  const inner = W - M * 2;
  const draws: Draw[] = [];
  const rects = new Map<string, Rect>();

  // Masthead
  const date = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(gazette.publishedAt);
  draws.push((c) => {
    c.fillStyle = INK;
    c.fillRect(M, 84, inner, 9);
    c.fillRect(M, 101, inner, 2);
    c.textAlign = 'center';
    c.font = fonts.news(236, 900);
    c.fillText(t('app.name', undefined, locale), W / 2, 318);
    c.font = fonts.news(42, 400, true);
    c.fillText(t('gz.motto', undefined, locale), W / 2, 384);
    c.fillRect(M, 414, inner, 2);
    c.fillRect(M, 421, inner, 1);
    c.font = fonts.news(30, 700);
    c.fillText(date.charAt(0).toUpperCase() + date.slice(1), W / 2, 466);
    c.textAlign = 'left';
    c.fillText(t('gz.edition', { n: gazette.edition }, locale), M, 466);
    c.textAlign = 'right';
    c.fillText(tn('gz.count', gazette.articles.length, undefined, locale), W - M, 466);
    c.textAlign = 'left';
    c.fillRect(M, 488, inner, 1);
    c.fillRect(M, 494, inner, 2);
  });
  let y = 540;

  const [leadArticle, ...rest] = gazette.articles;
  if (leadArticle) {
    const block = articleBlock(leadArticle, input, inner, true);
    const top = y;
    draws.push((c) => block.draw(c, M, top));
    rects.set(leadArticle.id, { x: M, y: top, w: inner, h: block.height });
    y += block.height + 36;
  }

  const columnCount = rest.length > 14 ? 4 : 3;
  const columnWidth = (inner - G * (columnCount - 1)) / columnCount;
  const columns = Array.from({ length: columnCount }, () => y);
  for (const article of rest) {
    const block = articleBlock(article, input, columnWidth, false);
    const column = columns.indexOf(Math.min(...columns));
    const x = M + column * (columnWidth + G);
    const top = columns[column]!;
    draws.push((c) => block.draw(c, x, top));
    rects.set(article.id, { x, y: top, w: columnWidth, h: block.height });
    columns[column] = top + block.height + 26;
  }

  // Classified ads fill the ragged bottom of the columns, like any paper that went to press.
  const adKeys: MessageKey[] = ['gz.ad.1', 'gz.ad.2', 'gz.ad.3', 'gz.ad.4', 'gz.ad.5'];
  let adIndex = gazette.edition % adKeys.length;
  const bottom = Math.max(...columns, y + 260);
  columns.forEach((columnTop, column) => {
    let cursor = columnTop;
    for (let placed = 0; placed < 2; placed++) {
      const block = adBlock(adKeys[adIndex % adKeys.length]!, locale, columnWidth);
      if (cursor + block.height > bottom + 40) break;
      const x = M + column * (columnWidth + G);
      const top = cursor;
      draws.push((c) => block.draw(c, x, top));
      cursor += block.height + 16;
      adIndex += 1;
    }
  });
  y = bottom + 30;

  const palmares = palmaresBlock(input, inner);
  if (palmares) {
    const top = y;
    draws.push((c) => palmares.draw(c, M, top));
    y += palmares.height + 20;
  }

  const footer = input.footer ?? t('gz.footer', undefined, locale);
  const footerTop = y;
  draws.push((c) => {
    c.fillStyle = INK;
    c.fillRect(M, footerTop, inner, 2);
    c.font = fonts.news(28, 400, true);
    c.textAlign = 'center';
    c.fillText(footer, W / 2, footerTop + 48);
    c.textAlign = 'left';
  });
  const height = footerTop + 48 + M;

  const scale = Math.min(1, MAX_TEXTURE / height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(W * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.scale(scale, scale);
  ctx.fillStyle = NEWSPRINT;
  ctx.fillRect(0, 0, W, height);
  paperGrain(ctx, W, height, gazette.edition * 97 + gazette.articles.length, 0.6);
  ctx.textBaseline = 'alphabetic';
  for (const draw of draws) draw(ctx);

  const uv = new Map<string, Rect>();
  for (const [id, rect] of rects) uv.set(id, { x: rect.x / W, y: rect.y / height, w: rect.w / W, h: rect.h / height });
  return { canvas, rects: uv, aspect: W / height };
}
